// ============================================================
// VCS.ChatMedia — Medien-Handling im Chat
// - Bild/Video/Datei-Upload (Multipart, Fortschritt)
// - Medien-Preview vor dem Senden (Crop, Resize)
// - Drag & Drop auf Chat-Fenster
// - Inline-Lightbox fuer Bilder
// - Download und Weiterleiten von Medien
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatMedia = class {

    // ── Konstanten ───────────────────────────────────────────

    static ALLOWED_TYPES = {
        'image/png' : 'image',
        'image/jpeg': 'image',
        'image/gif' : 'image',
        'video/mp4' : 'video',
        'video/webm': 'video',
        'video/quicktime': 'video', // Safari/iOS Fotomediathek liefert .mov
        'audio/mpeg': 'music',
        'application/pdf': 'document',
    };

    // Unverschluesselte Videos verkleinert der Server nach dem Upload, darum ist
    // dort mehr erlaubt. Alles andere (Bilder, PDFs) und verschluesselte Medien
    // bleiben unangetastet liegen — und damit beim kleinen Limit.
    // Muss zu MEDIA_MAX_SIZE / MEDIA_MAX_SIZE_ENCRYPTED im Backend passen.
    static MAX_SIZE = 50 * 1024 * 1024;         // 50 MB
    static MAX_SIZE_VIDEO = 300 * 1024 * 1024;  // 300 MB

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._mediaPreviewFile = null;
        this._mediaPreviewObjectUrl = null;
        this._mediaPreviewTranslateMode = null;
        this._mediaDragCounter = 0;
        this._captionEmojiOpen = false;
        this._captionCursorPos = 0;
        this._view = {};
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    getMediaType(file) {
        return VCS.ChatMedia.ALLOWED_TYPES[file.type] || null;
    }

    // maxUploadSize(mediaType) — Limit fuer den aktiven Chat in Bytes.
    // Das grosse Limit gilt nur fuer Videos in Chats ohne E2E: nur die kann der
    // Server nach dem Upload verkleinern.
    maxUploadSize(mediaType) {
        const isE2E = !!VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId];
        return (mediaType === 'video' && !isE2E)
            ? VCS.ChatMedia.MAX_SIZE_VIDEO
            : VCS.ChatMedia.MAX_SIZE;
    }

    // tooLargeKey(mediaType) — passender i18n-Key zum jeweiligen Limit.
    // Drei Faelle: Video ohne E2E (300 MB), Video mit E2E (50 MB, mit
    // Erklaerung warum), alles andere (50 MB).
    tooLargeKey(mediaType) {
        if (mediaType !== 'video') return 'mediaFileTooLarge';
        return VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId]
            ? 'mediaVideoTooLargeE2E'
            : 'mediaVideoTooLarge';
    }

    // videoTranslateMode(chatId) — wie Videos in diesem Chat uebersetzt werden:
    //  'off'    verschluesselter Chat, keine Uebersetzung moeglich
    //  'group'  Gruppe: bisheriges Verhalten, keine Auswahl
    //  'same'   Gegenueber spricht dieselbe Sprache: keine Verarbeitung
    //  'choose' andere oder unbekannte Sprache: Checkbox anzeigen
    videoTranslateMode(chatId) {
        if (VCS.state.chatE2EEnabled[chatId]) return 'off';
        const chat = (VCS.state.chatChats || []).find(c => c.id === chatId);
        const activeGroup = chatId === VCS.state.chatActiveChatId && VCS.state.chatActiveIsGroup;
        if (chat?.isGroup || chat?._isGroup || activeGroup) return 'group';
        const peerLang = VCS.i18n?.normalizeLang?.(chat?.peerLang) || '';
        const ownLang = VCS.i18n?.normalizeLang?.(VCS.state.currentLang) || '';
        if (peerLang && ownLang && peerLang === ownLang) return 'same';
        return 'choose';
    }

    showMediaPreview(file) {
        const mediaType = this.getMediaType(file);
        if (!mediaType) {
            VCS.ui?.showToast?.(t('mediaInvalidType'), 'error');
            return;
        }
        if (file.size > this.maxUploadSize(mediaType)) {
            VCS.ui?.showToast?.(t(this.tooLargeKey(mediaType)), 'error');
            return;
        }

        this._mediaPreviewFile = file;
        if (this._mediaPreviewObjectUrl) {
            URL.revokeObjectURL(this._mediaPreviewObjectUrl);
        }
        this._mediaPreviewObjectUrl = URL.createObjectURL(file);

        const titleMap = {
            image: t('chatPhotoPreview'),
            video: t('chatVideo'),
            music: t('chatMusic'),
            document: t('chatDocument')
        };
        $('#mediaPreviewTitle')[0].textContent = titleMap[mediaType] || file.name;

        const content = $('#mediaPreviewContent')[0];
        if (mediaType === 'image') {
            content.innerHTML = `<img src="${this._mediaPreviewObjectUrl}" alt="">`;
        } else if (mediaType === 'video') {
            content.innerHTML = `<video src="${this._mediaPreviewObjectUrl}" controls playsinline></video>`;
        } else if (mediaType === 'document') {
            // Keine Inline-Vorschau fuer Dokumente: nur Icon + Dateiname zeigen
            content.innerHTML = `<div class="media-preview-doc">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                </svg>
                <span class="media-preview-doc-name"></span>
            </div>`;
            content.querySelector('.media-preview-doc-name').textContent = file.name;
        } else {
            content.innerHTML = `<audio src="${this._mediaPreviewObjectUrl}" controls></audio>`;
        }

        $('#mediaPreviewInfo')[0].textContent = `${file.name} — ${this._formatFileSize(file.size)}`;
        $('#mediaPreviewCaption')[0].value = '';

        // Video-Untertitel-Toggle: nur fuer Video, nicht in E2E-Chats und nicht, wenn
        // das Gegenueber dieselbe Sprache spricht (dann keine Verarbeitung).
        this._mediaPreviewTranslateMode = mediaType === 'video'
            ? this.videoTranslateMode(VCS.state.chatActiveChatId)
            : null;
        const subRow = $('#mediaPreviewSubtitleRow')[0];
        const subToggle = $('#mediaPreviewSubtitleToggle')[0];
        if (subRow && subToggle) {
            const showToggle = this._mediaPreviewTranslateMode === 'choose'
                || this._mediaPreviewTranslateMode === 'group';
            subRow.classList.toggle('hidden', !showToggle);
            // Default: aus. User entscheidet bewusst (API-Kosten + Verarbeitungszeit).
            subToggle.checked = false;
        }

        $('#mediaPreviewOverlay')[0].classList.remove('hidden');

        requestAnimationFrame(() => $('#mediaPreviewCaption')[0].focus());
    }

    closeMediaPreview() {
        this._closeCaptionEmoji();
        $('#mediaPreviewOverlay')[0]?.classList.add('hidden');
        if (this._mediaPreviewObjectUrl) {
            URL.revokeObjectURL(this._mediaPreviewObjectUrl);
            this._mediaPreviewObjectUrl = null;
        }
        this._mediaPreviewFile = null;
        const content = $('#mediaPreviewContent')[0];
        if (content) {
            content.innerHTML = '';
        }
    }

    async uploadMedia(file, caption, opts) {
        if (!VCS.state.chatActiveChatId || !file) {
            return;
        }

        const mediaType = this.getMediaType(file);
        if (!mediaType) {
            return;
        }

        let uploadFile = file;
        let mediaEncrypted = false;
        let mediaIv = null;
        let encCaption = caption || '';
        let mediaKeyVersion = 1;
        let mediaRecipientKeyVersion = 1;

        if (VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId] && typeof E2E !== 'undefined') {
            const sharedKey = await VCS.e2e?.getOrDeriveKey?.(VCS.state.chatActiveChatId);
            if (sharedKey) {
                const enc = await E2E.encryptBlob(sharedKey, file);
                uploadFile = new File([enc.encryptedBlob], file.name, {type: 'application/octet-stream'});
                mediaIv = enc.iv;
                mediaEncrypted = true;
                if (sharedKey._versionInfo) {
                    mediaKeyVersion = sharedKey._versionInfo.senderVersion;
                    mediaRecipientKeyVersion = sharedKey._versionInfo.recipientVersion;
                }
                if (caption) {
                    const encText = await E2E.encryptText(sharedKey, caption);
                    encCaption = JSON.stringify({ct: encText.ciphertext, iv: encText.iv});
                }
            }
        }

        const translateRequested = !mediaEncrypted && !VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId];
        // Video-Uebersetzung abgewaehlt oder gleiche Sprache (siehe videoTranslateMode).
        // translate bleibt davon unberuehrt, es steuert auch die Uebersetzung der
        // Bildunterschrift.
        const skipVideoTranslation = !!(opts && opts.skipVideoTranslation)
            && mediaType === 'video' && translateRequested;
        // Galerie-Upload: Quellsprache per Auto-Detect statt aus dem Profil.
        const generateSubtitles = !!(opts && opts.generateSubtitles)
            && mediaType === 'video' && translateRequested && !skipVideoTranslation;
        const deferVideoUntilReady = mediaType === 'video'
            && (translateRequested || generateSubtitles) && !skipVideoTranslation;

        const formData = new FormData();
        formData.append('media', uploadFile, file.name);
        formData.append('chatId', VCS.state.chatActiveChatId);
        formData.append('senderLang', VCS.state.currentLang);
        formData.append('caption', encCaption);
        formData.append('translate', translateRequested ? 'true' : 'false');
        if (generateSubtitles) {
            // Server-Trigger: Galerie-Upload mit Auto-Detect-Source-Lang via Deepgram.
            formData.append('generateSubtitles', 'true');
        }
        if (skipVideoTranslation) {
            formData.append('skipVideoTranslation', 'true');
        }
        if (VCS.state.chatReplyToId) {
            formData.append('replyToId', VCS.state.chatReplyToId);
        }
        if (mediaEncrypted) {
            formData.append('encrypted', 'true');
            formData.append('iv', mediaIv);
            formData.append('keyVersion', mediaKeyVersion.toString());
            formData.append('recipientKeyVersion', mediaRecipientKeyVersion.toString());
        }

        const localBlobUrl = URL.createObjectURL(file);
        const tempMsg = {
            id            : 'temp_' + Date.now(),
            chatId        : VCS.state.chatActiveChatId,
            senderPhone   : VCS.state.chatMyPhone,
            originalText  : caption || '',
            translatedText: null,
            senderLang    : VCS.state.currentLang,
            timestamp     : Date.now(),
            status        : 'sent',
            mediaUrl      : 'pending',
            mediaType     : mediaType,
            mediaFileName : file.name,
            mediaSizeBytes: file.size,
            _localBlobUrl : localBlobUrl,
            _sending      : true,
        };
        // Video-mit-Uebersetzung: temp-Bubble sofort als "wird verarbeitet" markieren,
        // damit der Hinweis ohne Warten auf die Server-Antwort erscheint. Greift
        // isDeferredVideoMessage() -> chat-video-processing-Bubble. Wird beim
        // Server-Response durch die echte (ebenfalls processing) Message ersetzt.
        if (deferVideoUntilReady) {
            tempMsg.translatedVideoStatus = 'processing';
        }
        // Video: waehrend des Hochladens den echten Fortschritt zeigen. Erst
        // danach uebernimmt die Verarbeitungs-Bubble - ein Video kann gross sein,
        // und ein pauschaler Hinweis sagt dem Absender nichts ueber den Stand.
        if (mediaType === 'video') {
            tempMsg._uploadProgress = 0;
        }

        // Optimistische temp-Bubble immer sofort zeigen (im deferred-Fall als
        // Processing-Variante). Vorher wurde sie bei deferVideoUntilReady unterdrueckt,
        // wodurch der Chat bis zur Server-Antwort leer blieb.
        if (!VCS.state.chatMessages[VCS.state.chatActiveChatId]) {
            VCS.state.chatMessages[VCS.state.chatActiveChatId] = [];
        }
        VCS.state.chatMessages[VCS.state.chatActiveChatId].push(tempMsg);
        VCS.chatMessages?.renderChatMessages?.();
        VCS.chatMessages?.scrollToBottom?.();
        VCS.chatMessages?.clearReply?.();

        try {
            // Fortschritt nur beim Video anzeigen, und nur wenn sich der Wert
            // wirklich geaendert hat - sonst laeuft das DOM-Update leer.
            const sent = await this.postMediaWithProgress(formData, (pct) => {
                if (mediaType !== 'video' || tempMsg._uploadProgress === pct) return;
                tempMsg._uploadProgress = pct;
                VCS.chatMessages?.updateUploadProgress?.(tempMsg.id, pct);
            });

            if (deferVideoUntilReady && VCS.chatVideo?.isDeferredVideoMessage?.(sent)) {
                sent._deferredVideoPending = true;
                VCS.chatVideo?.ensureVideoProcessingPoll?.(VCS.state.chatActiveChatId);
            }
            if (!VCS.state.chatMessages[VCS.state.chatActiveChatId]) {
                VCS.state.chatMessages[VCS.state.chatActiveChatId] = [];
            }
            const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId];
            const tempIdx = msgs.findIndex(m => m.id === tempMsg.id);
            if (tempIdx !== -1) {
                msgs.splice(tempIdx, 1);
            }
            if (!msgs.some(m => m.id === sent.id)) {
                VCS.chatMessages?.applyPendingStatus?.(sent);
                msgs.push(sent);
            }
            VCS.chatMessages?.renderChatMessages?.();
            VCS.chatMessages?.scrollToBottom?.();
            const previewEmoji = mediaType === 'image' ? '\u{1F4F7}' : mediaType === 'video' ? '\u{1F3AC}' : mediaType === 'document' ? '\u{1F4C4}' : '\u{1F3B5}';
            VCS.chatMessages?.updateChatPreview?.(VCS.state.chatActiveChatId, previewEmoji + ' ' + (caption || file.name), Date.now());
            VCS.chatMediaGallery?.invalidateCache?.(VCS.state.chatActiveChatId);
        } catch (e) {
            console.error('[chat] uploadMedia error:', e, 'status=' + (e?._status || 'n/a'), 'code=' + (e?._code || 'n/a'));
            // Upload fehlgeschlagen: optimistische temp-Bubble entfernen, sonst haengt
            // die "wird verarbeitet"-Anzeige dauerhaft im Chat.
            const failMsgs = VCS.state.chatMessages[VCS.state.chatActiveChatId];
            if (failMsgs) {
                const failIdx = failMsgs.findIndex(m => m.id === tempMsg.id);
                if (failIdx !== -1) {
                    failMsgs.splice(failIdx, 1);
                    VCS.chatMessages?.renderChatMessages?.();
                }
            }
            // Server-Text ist deutsch — daher den lokalisierten Key nutzen.
            const key = e?._code === 'media_too_large'
                ? this.tooLargeKey(mediaType)
                : 'mediaUploadFailed';
            VCS.ui?.showToast?.(t(key), 'error');
        } finally {
            URL.revokeObjectURL(localBlobUrl);
        }
    }

    // Upload per XMLHttpRequest statt fetch: nur XHR meldet, wie viel der Datei
    // schon draussen ist. Rueckgabe und Fehlerform bleiben wie beim fetch-Weg -
    // aufgeloest wird das Antwort-JSON, im Fehlerfall fliegt ein Error mit
    // _status und _code, den der Aufrufer schon auswertet.
    postMediaWithProgress(formData, onProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', VCS.Chat.API_BASE + '/messages/media');

            // Kein Content-Type setzen - den braucht FormData mit eigener
            // Boundary, die der Browser selbst erzeugt.
            const headers = VCS.chat?.authHeaders?.() || {};
            Object.keys(headers).forEach(k => xhr.setRequestHeader(k, headers[k]));

            xhr.upload.onprogress = (e) => {
                if (!e.lengthComputable) return;
                onProgress(Math.round((e.loaded / e.total) * 100));
            };

            xhr.onload = () => {
                let body = {};
                try {
                    body = JSON.parse(xhr.responseText);
                } catch (_) { /* Server ohne JSON-Antwort - unten als Fehler behandelt */ }
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(body);
                    return;
                }
                const err = new Error(body.error || `HTTP ${xhr.status}`);
                err._code = body.code || null;
                err._status = xhr.status;
                reject(err);
            };

            xhr.onerror = () => {
                const err = new Error('Netzwerkfehler beim Upload');
                err._status = 0;
                reject(err);
            };
            xhr.onabort = () => reject(new Error('Upload abgebrochen'));

            xhr.send(formData);
        });
    }

    openLightbox(src) {
        const existing = document.querySelector('.media-lightbox');
        if (existing) {
            existing.remove();
        }

        const lb = document.createElement('div');
        lb.className = 'media-lightbox';
        lb.innerHTML = `<img src="${escapeHtml(src)}" alt="">`;
        lb.addEventListener('click', () => lb.remove());
        document.body.appendChild(lb);

        const closeLb = (e) => {
            if (e.key === 'Escape') {
                lb.remove();
                document.removeEventListener('keydown', closeLb);
            }
        };
        document.addEventListener('keydown', closeLb);
    }

    initDragDrop() {
        const screen = $('#chatDetailScreen')[0];
        if (!screen) {
            return;
        }

        document.addEventListener('dragover', (e) => e.preventDefault(), false);
        document.addEventListener('drop', (e) => e.preventDefault(), false);

        screen.addEventListener('dragenter', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._mediaDragCounter++;
            const activeId = VCS.state.chatActiveChatId;
            if (this._mediaDragCounter === 1 && activeId && !VCS.Chat?.isBotChat?.(activeId)) {
                $('#chatDropZone')[0]?.classList.remove('hidden');
            }
        });

        screen.addEventListener('dragleave', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._mediaDragCounter--;
            if (this._mediaDragCounter <= 0) {
                this._mediaDragCounter = 0;
                $('#chatDropZone')[0]?.classList.add('hidden');
            }
        });

        screen.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        screen.addEventListener('drop', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._mediaDragCounter = 0;
            $('#chatDropZone')[0]?.classList.add('hidden');

            const file     = e.dataTransfer?.files?.[0];
            const activeId = VCS.state.chatActiveChatId;
            if (!file || !activeId) {
                return;
            }
            if (VCS.Chat?.isBotChat?.(activeId)) {
                VCS.ui?.showToast?.(t('botMediaNotAllowed'), 'warning', 3500);
                return;
            }
            this.showMediaPreview(file);
        });
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        this.initDragDrop();

        $('#mediaPreviewClose')[0]?.addEventListener('click', () => this.closeMediaPreview());
        $('#mediaPreviewOverlay')[0]?.addEventListener('click', (e) => {
            if (e.target === $('#mediaPreviewOverlay')[0]) {
                this.closeMediaPreview();
            }
        });
        $('#mediaPreviewSend')[0]?.addEventListener('click', () => {
            if (this._mediaPreviewFile) {
                const caption = $('#mediaPreviewCaption')[0]?.value?.trim() || '';
                const subToggle = $('#mediaPreviewSubtitleToggle')[0];
                const subtitlesChecked = !!(subToggle && !subToggle.disabled && subToggle.checked);
                const mode = this._mediaPreviewTranslateMode;
                const opts = {
                    generateSubtitles: subtitlesChecked,
                    // 1:1-Chat: ohne Haken bzw. bei gleicher Sprache keine Verarbeitung.
                    // Gruppen bleiben beim bisherigen Verhalten.
                    skipVideoTranslation: mode === 'same' || (mode === 'choose' && !subtitlesChecked),
                };
                this.uploadMedia(this._mediaPreviewFile, caption, opts);
                this.closeMediaPreview();
            }
        });
        $('#mediaPreviewCaption')[0]?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                $('#mediaPreviewSend')[0]?.click();
            }
        });
        $('#mediaPreviewEmojiBtn')[0]?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleCaptionEmoji();
        });
        // Klick ausserhalb von Button und Popup schliesst den Emoji-Picker
        $('#mediaPreviewOverlay')[0]?.addEventListener('click', (e) => {
            if (this._captionEmojiOpen
                && !e.target.closest('#mediaPreviewEmojiWrap')
                && !e.target.closest('#mediaPreviewEmojiBtn')) {
                this._closeCaptionEmoji();
            }
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !$('#mediaPreviewOverlay')[0]?.classList.contains('hidden')) {
                this.closeMediaPreview();
            }
        });

        $('#chatAttachBtn')[0]?.addEventListener('click', () => {
            $('#chatMediaFileInput')[0]?.click();
        });
        $('#chatMediaFileInput')[0]?.addEventListener('change', (e) => {
            const file = e.target.files?.[0];
            if (file) {
                this.showMediaPreview(file);
            }
            e.target.value = '';
        });
    }

    // ── Emoji-Picker fuer das Beschriftungsfeld ──────────────

    toggleCaptionEmoji() {
        if (this._captionEmojiOpen) {
            this._closeCaptionEmoji();
        } else {
            this._openCaptionEmoji();
        }
    }

    _openCaptionEmoji() {
        const wrap  = $('#mediaPreviewEmojiWrap')[0];
        const input = $('#mediaPreviewCaption')[0];
        if (!wrap || !input) {
            return;
        }
        // Cursor-Position merken: Tap auf den Picker kann dem Feld den Fokus nehmen
        this._captionCursorPos = input.selectionStart ?? input.value.length;
        // Mobil: Tastatur ausblenden, damit Feld + Picker zugleich sichtbar bleiben
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
            input.blur();
        }
        // Gemeinsame Picker-Factory aus chat-messages.js wiederverwenden
        // Sticker sind keine Bildunterschrift -> Caption-Picker nur mit Emoji.
        const picker = VCS.chatMessages?.createEmojiPickerElement?.((emoji) => this._insertCaptionEmoji(emoji), { stickers: false });
        if (!picker) {
            return;
        }
        wrap.innerHTML = '';
        wrap.appendChild(picker);
        wrap.classList.remove('hidden');
        this._captionEmojiOpen = true;
        $('#mediaPreviewEmojiBtn')[0]?.classList.add('media-preview-emoji-btn--active');
    }

    _closeCaptionEmoji() {
        const wrap = $('#mediaPreviewEmojiWrap')[0];
        if (wrap) {
            wrap.classList.add('hidden');
            wrap.innerHTML = '';
        }
        this._captionEmojiOpen = false;
        $('#mediaPreviewEmojiBtn')[0]?.classList.remove('media-preview-emoji-btn--active');
    }

    _insertCaptionEmoji(emoji) {
        const input = $('#mediaPreviewCaption')[0];
        if (!input) {
            return;
        }
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        // Mobil: gemerkte Position (Feld ist geblurrt), Desktop: aktuelle Selektion
        const start = isMobile
            ? (this._captionCursorPos ?? input.value.length)
            : (input.selectionStart ?? input.value.length);
        const end = isMobile ? start : (input.selectionEnd ?? start);
        input.value = input.value.slice(0, start) + emoji + input.value.slice(end);
        const pos = start + emoji.length;
        // Position fortschreiben fuer mehrere Emojis hintereinander
        this._captionCursorPos = pos;
        if (!isMobile) {
            input.focus();
            input.setSelectionRange(pos, pos);
        }
    }

    // ── PDF-Viewer ───────────────────────────────────────────
    //
    // Zeigt PDFs im Chat an, statt sie nur herunterzuladen. Warum pdf.js und
    // kein <embed>/<iframe>: iOS-Safari rendert eingebettete PDFs nicht
    // zuverlaessig (oft nur die erste Seite oder ein leerer Rahmen), und
    // E2E-PDFs muessten als Blob-URL eingebettet werden — dort ist iOS noch
    // wackliger. pdf.js zeichnet selbst auf ein Canvas und laeuft ueberall.
    //
    // Geladen wird die Bibliothek ERST beim ersten Anzeigen (~1,8 MB) — wer nie
    // ein PDF oeffnet, laedt kein Byte davon. Deshalb steht sie auch nicht in
    // index.php: so bleibt die Service-Worker-CACHE_VERSION unberuehrt.
    // Legacy-Build wegen aelterer iOS-Safari-Versionen.

    async _ensurePdfJs() {
        if (this._pdfjs) return this._pdfjs;
        if (this._pdfjsLoading) return this._pdfjsLoading;

        this._pdfjsLoading = (async () => {
            const lib = await import('/js/pdfjs/pdf.min.mjs');
            lib.GlobalWorkerOptions.workerSrc = '/js/pdfjs/pdf.worker.min.mjs';
            this._pdfjs = lib;
            return lib;
        })();

        try {
            return await this._pdfjsLoading;
        } catch (e) {
            // Naechster Versuch darf es erneut probieren (z. B. Netz war weg).
            this._pdfjsLoading = null;
            throw e;
        }
    }

    // openPdfViewer(msg) — Overlay mit der ersten Seite, blaetterbar.
    // Eine Seite zur Zeit statt aller Seiten untereinander: Auf dem Handy
    // frisst ein Canvas pro Seite zu viel Speicher.
    async openPdfViewer(msg) {
        const existing = document.querySelector('.pdf-viewer-overlay');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.className = 'pdf-viewer-overlay';
        overlay.innerHTML = `
            <div class="pdf-viewer-bar">
                <div class="pdf-viewer-title">${escapeHtml(msg.mediaFileName || 'PDF')}</div>
                <div class="pdf-viewer-pages"></div>
                <button class="pdf-viewer-close" type="button" aria-label="${escapeHtml(t('close'))}">
                    <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
            <div class="pdf-viewer-stage"><div class="pdf-viewer-spinner"></div></div>
            <div class="pdf-viewer-nav hidden">
                <button class="pdf-viewer-prev" type="button" aria-label="&#8592;">&#8249;</button>
                <button class="pdf-viewer-next" type="button" aria-label="&#8594;">&#8250;</button>
            </div>`;
        document.body.appendChild(overlay);

        // Zweiter Ausweg neben dem X: Die Zurueck-Geste schliesst den Viewer
        // (navigation.js::_handleBackNavigation, Punkt 0) — auf dem iPhone
        // gibt es weder Escape noch einen Browser-Zurueck-Button. Kein
        // pushState hier: Den Dummy-Eintrag legt navigation.js::init() an,
        // erneuert wird er beim Schliessen — wie bei den anderen Overlays.
        this._pdfKeyHandler = (e) => {
            if (e.key === 'Escape') this.closePdfViewer();
            if (e.key === 'ArrowLeft') this._pdfGo(-1);
            if (e.key === 'ArrowRight') this._pdfGo(1);
        };
        overlay.querySelector('.pdf-viewer-close')
            .addEventListener('click', () => this.closePdfViewer());
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.closePdfViewer();
        });
        document.addEventListener('keydown', this._pdfKeyHandler);

        const stage = overlay.querySelector('.pdf-viewer-stage');
        try {
            // Entschluesselt, falls E2E — dieselbe Quelle wie der Download.
            const data = await VCS.chatMessages?._fetchMessageMediaForExport?.(msg);
            if (!data) throw new Error('Datei nicht ladbar');

            const lib = await this._ensurePdfJs();
            const buf = await data.blob.arrayBuffer();
            this._pdfDoc = await lib.getDocument({
                data: new Uint8Array(buf),
                // Kein eval fuer Type-1-Fonts: ein PDF ist hier eine fremde
                // Datei von jemand anderem, kein vertrauenswuerdiger Input.
                isEvalSupported: false,
            }).promise;

            overlay.querySelector('.pdf-viewer-nav')
                .classList.toggle('hidden', this._pdfDoc.numPages < 2);
            this._pdfPage = 1;
            await this._pdfRender();

            overlay.querySelector('.pdf-viewer-prev')
                .addEventListener('click', () => this._pdfGo(-1));
            overlay.querySelector('.pdf-viewer-next')
                .addEventListener('click', () => this._pdfGo(1));
        } catch (e) {
            console.error('[chat] pdf viewer:', e);
            stage.innerHTML = `<div class="pdf-viewer-error">${escapeHtml(t('pdfViewError'))}</div>`;
        }
    }

    // closePdfViewer() — Overlay weg, Listener ab, pdf.js-Dokument freigeben.
    // Das destroy() ist wichtig: Ohne bleibt das geparste PDF im Speicher und
    // der Worker beschaeftigt — auf dem Handy nach ein paar PDFs spuerbar.
    closePdfViewer() {
        if (this._pdfKeyHandler) {
            document.removeEventListener('keydown', this._pdfKeyHandler);
            this._pdfKeyHandler = null;
        }
        document.querySelector('.pdf-viewer-overlay')?.remove();
        if (this._pdfDoc) {
            try {
                this._pdfDoc.destroy();
            } catch (e) {
                console.warn('[chat] pdf destroy:', e);
            }
            this._pdfDoc = null;
        }
        this._pdfPage = 1;
        this._pdfRendering = false;
    }

    async _pdfGo(delta) {
        if (!this._pdfDoc || this._pdfRendering) return;
        const next = this._pdfPage + delta;
        if (next < 1 || next > this._pdfDoc.numPages) return;
        this._pdfPage = next;
        await this._pdfRender();
    }

    async _pdfRender() {
        const overlay = document.querySelector('.pdf-viewer-overlay');
        if (!overlay || !this._pdfDoc) return;

        this._pdfRendering = true;
        try {
            const page = await this._pdfDoc.getPage(this._pdfPage);
            const stage = overlay.querySelector('.pdf-viewer-stage');

            // An die Buehne einpassen, aber scharf auf Retina-Displays.
            const base = page.getViewport({ scale: 1 });
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const fit = Math.min(
                (stage.clientWidth - 8) / base.width,
                (stage.clientHeight - 8) / base.height
            );
            const viewport = page.getViewport({ scale: Math.max(fit, 0.1) * dpr });

            const canvas = document.createElement('canvas');
            canvas.className = 'pdf-viewer-canvas';
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = Math.floor(viewport.width / dpr) + 'px';
            canvas.style.height = Math.floor(viewport.height / dpr) + 'px';

            await page.render({
                canvasContext: canvas.getContext('2d'),
                viewport,
            }).promise;

            stage.innerHTML = '';
            stage.appendChild(canvas);
            overlay.querySelector('.pdf-viewer-pages').textContent =
                this._pdfPage + ' / ' + this._pdfDoc.numPages;
        } catch (e) {
            console.error('[chat] pdf render:', e);
        } finally {
            this._pdfRendering = false;
        }
    }

    // ── Private Methoden ─────────────────────────────────────

    _formatFileSize(bytes) {
        if (bytes < 1024) {
            return bytes + ' B';
        }
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
};
